import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { InputSearchComponent } from '../../domains/shared/components/input-search/input-search.component';
import { ISidebarMenuItem } from './interfaces/sidebar-menu-item.interface';
import { ISidebarMenuSettings } from './interfaces/sidebar-menu-settings.interface';
import { SIDEBAR_MENU_DEFAULT_SETTINGS } from './models/sidebar-menu-settings';

const SIDEBAR_ANIMATION_DURATION_MS = 300;

@Component({
  selector: 'sgt-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input() items: ISidebarMenuItem[] = [];
  @Input() isSidebarMini = false;
  @Input() settings: Required<ISidebarMenuSettings> = { ...SIDEBAR_MENU_DEFAULT_SETTINGS };

  @Output() searchChange = new EventEmitter<string>();
  @Output() itemClick = new EventEmitter<void>();
  @Output() isSidebarMiniChange = new EventEmitter<boolean>();

  @ViewChild('searchInput') searchInput?: InputSearchComponent;

  transitionEnabled = false;
  animateChildren = false;

  submenuItem: ISidebarMenuItem | null = null;
  submenuStyle: { [key: string]: string } = {};
  private submenuCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly SUBMENU_CLOSE_DELAY = 150;

  private currentSearchTerm = '';
  private focusSearchAfterExpand = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.expandActiveGroups();
        this.cdr.markForCheck();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['settings']) {
      this.settings = {
        ...SIDEBAR_MENU_DEFAULT_SETTINGS,
        ...changes['settings'].currentValue,
      };
    }

    if (changes['items'] && this.items.length > 0) {
      this.expandActiveGroups();
    }

    if (changes['isSidebarMini']) {
      if (!changes['isSidebarMini'].isFirstChange()) {
        this.transitionEnabled = true;
      }
      this.animateChildren = false;
      this.closeSubmenu();
      if (!this.isSidebarMini) {
        this.expandActiveGroups();
        if (this.focusSearchAfterExpand) {
          this.focusSearchAfterExpand = false;
          setTimeout(() => this.searchInput?.focus(), SIDEBAR_ANIMATION_DURATION_MS);
        }
      }
    }
  }

  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.submenuItem) {
      this.closeSubmenu();
      this.cdr.markForCheck();
    }
  }

  goToHome(): void {
    this.router.navigate(['/ceppem-ai']);
  }

  expandSidebar(): void {
    this.isSidebarMiniChange.emit(false);
    this.itemClick.emit();
  }

  onSearchAreaClick(): void {
    if (this.isSidebarMini) {
      this.focusSearchAfterExpand = true;
      this.expandSidebar();
      return;
    }
    this.searchInput?.focus();
  }

  onSearchInput(term: string): void {
    this.animateChildren = false;
    this.currentSearchTerm = term;
    this.cdr.markForCheck();
    this.searchChange.emit(term);
  }

  toggleGroup(item: ISidebarMenuItem): void {
    item.expanded = !item.expanded;
    this.animateChildren = true;
    this.cdr.markForCheck();
  }

  onGroupClick(item: ISidebarMenuItem): void {
    if (this.isSidebarMini) {
      return;
    }
    this.toggleGroup(item);
  }

  onGroupEnter(item: ISidebarMenuItem, event: MouseEvent): void {
    if (!this.isSidebarMini) {
      return;
    }
    this.cancelSubmenuClose();
    const triggerEl = event.currentTarget as HTMLElement;
    const sidebarEl = triggerEl.closest('.sidebar-menu') as HTMLElement | null;
    this.submenuStyle = this.buildSubmenuStyle(
      triggerEl.getBoundingClientRect(),
      sidebarEl ? sidebarEl.getBoundingClientRect().right : triggerEl.getBoundingClientRect().right
    );
    this.submenuItem = item;
  }

  onGroupLeave(): void {
    this.scheduleSubmenuClose();
  }

  onSubmenuEnter(): void {
    this.cancelSubmenuClose();
  }

  onSubmenuLeave(): void {
    this.scheduleSubmenuClose();
  }

  onSubmenuItemClick(): void {
    this.closeSubmenu();
    this.itemClick.emit();
  }

  onNavigate(): void {
    this.itemClick.emit();
  }

  hasChildren(item: ISidebarMenuItem): boolean {
    return !!item.children && item.children.length > 0;
  }

  get visibleItems(): ISidebarMenuItem[] {
    const term = this.normalize(this.currentSearchTerm.trim());
    if (!term) {
      return this.items;
    }
    return this.items.filter((item) => {
      if (this.normalize(item.label).includes(term)) {
        return true;
      }
      return (item.children || []).some((child) => this.normalize(child.label).includes(term));
    });
  }

  visibleChildren(item: ISidebarMenuItem): ISidebarMenuItem[] {
    const children = item.children || [];
    const term = this.normalize(this.currentSearchTerm.trim());
    if (!term || this.normalize(item.label).includes(term)) {
      return children;
    }
    return children.filter((child) => this.normalize(child.label).includes(term));
  }

  isExpanded(item: ISidebarMenuItem): boolean {
    const term = this.normalize(this.currentSearchTerm.trim());
    if (term) {
      return this.visibleChildren(item).length > 0;
    }
    return !!item.expanded;
  }

  trackByItem(_index: number, item: ISidebarMenuItem): string {
    return item.label;
  }

  ngOnDestroy(): void {
    this.cancelSubmenuClose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private scheduleSubmenuClose(): void {
    this.cancelSubmenuClose();
    this.submenuCloseTimer = setTimeout(() => {
      this.closeSubmenu();
      this.cdr.markForCheck();
    }, this.SUBMENU_CLOSE_DELAY);
  }

  private cancelSubmenuClose(): void {
    if (this.submenuCloseTimer !== null) {
      clearTimeout(this.submenuCloseTimer);
      this.submenuCloseTimer = null;
    }
  }

  private closeSubmenu(): void {
    this.cancelSubmenuClose();
    this.submenuItem = null;
  }

  private buildSubmenuStyle(rect: DOMRect, sidebarRight: number): { [key: string]: string } {
    const GAP = 4;
    const MARGIN = 8;
    const maxHeightCap = window.innerHeight * 0.7;

    const spaceBelow = window.innerHeight - rect.top - MARGIN;
    const openUpward = spaceBelow < maxHeightCap && rect.bottom - MARGIN > spaceBelow;
    const maxHeight = Math.min(maxHeightCap, openUpward ? rect.bottom - MARGIN : spaceBelow);

    return {
      position: 'fixed',
      left: `${sidebarRight + GAP}px`,
      maxHeight: `${maxHeight}px`,
      ...(openUpward
        ? { bottom: `${window.innerHeight - rect.bottom}px`, top: 'auto' }
        : { top: `${rect.top}px`, bottom: 'auto' }),
    };
  }

  private normalize(text: string): string {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  private expandActiveGroups(): void {
    this.items.forEach((item) => {
      if (this.hasChildren(item) && this.isAtivo(item)) {
        item.expanded = true;
      }
    });
  }

  isAtivo(item: ISidebarMenuItem): boolean {
    if (this.hasChildren(item)) {
      return (item.children || []).some((child) => this.isAtivo(child));
    }
    const url = this.router.url.split(/[?#]/)[0];
    return [item.path, ...(item.rotasAtivas || [])].some((path) => this.isPathActive(path, url));
  }

  private isPathActive(path: string | undefined, url: string): boolean {
    if (!path) {
      return false;
    }
    return url === path || url.startsWith(path + '/');
  }
}
